import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, get } from './api.js';

export interface Resource<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

export interface OpcoesDeRecurso {
  /**
   * Recarrega sozinho a cada N milissegundos. Serve para telas que precisam
   * acompanhar o que está acontecendo AGORA — o atendimento, principalmente:
   * mensagem nova que só aparece quando alguém lembra de apertar F5 não é
   * atendimento, é adivinhação.
   */
  atualizarACada?: number;
}

/**
 * Busca com recarga manual e, quando pedido, automática.
 *
 * Duas decisões que evitam o pior da atualização automática:
 * - a aba em segundo plano NÃO recarrega. Painel aberto a tarde inteira numa
 *   aba esquecida não vira tráfego nem custo;
 * - a recarga automática não acende o "carregando". Ninguém quer a tela
 *   piscando de 5 em 5 segundos enquanto lê uma conversa.
 */
export function useResource<T>(
  path: string | null,
  deps: unknown[] = [],
  opcoes: OpcoesDeRecurso = {},
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);
  const silencioso = useRef(false);
  /**
   * Guarda de ordem. Sem ela, uma resposta LENTA de um pedido antigo chega
   * depois e sobrescreve o dado do pedido novo. Com recarga automática a cada
   * 5 s isso deixa de ser teórico: o atendente abre a conversa B, a resposta
   * atrasada da conversa A aterrissa, e ele lê a pergunta de um cliente na
   * caixa de resposta de outro. Responder o cliente errado é o erro que a loja
   * não perdoa.
   */
  const pedido = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!silencioso.current) setLoading(true);
    silencioso.current = false;
    pedido.current += 1;
    const meu = pedido.current;
    get<T>(path)
      .then((result) => {
        if (!alive.current || meu !== pedido.current) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!alive.current || meu !== pedido.current) return;
        setError(err instanceof ApiError ? err : new ApiError('INTERNAL', String(err), 0));
      })
      .finally(() => {
        if (alive.current && meu === pedido.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const intervalo = opcoes.atualizarACada ?? 0;
  useEffect(() => {
    if (!path || intervalo <= 0) return;

    const atualizar = () => {
      // Aba escondida não gasta chamada: quando voltar, o listener abaixo
      // recarrega na hora e a tela fica correta de imediato.
      if (document.visibilityState !== 'visible') return;
      silencioso.current = true;
      setTick((t) => t + 1);
    };

    const id = setInterval(atualizar, intervalo);
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') atualizar();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [path, intervalo]);

  return { data, error, loading, reload };
}
